import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import config from "../../twh/config";
import { getFirstErrorMessage } from "../../twh/functions";

const FacebookMetaAdAccounts = () => {
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Fetch Ad Accounts
  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await fetch(
          `${config.reactApi_baseUrl}/react-api/integrations/facebook-meta-ad-accounts.php`,
          {
            method: 'GET',
            credentials: 'include',
          }
        );

        const data = await response.json();

        if (data && typeof data === 'object') {
          // object ko array mein convert karna
          const formatted = Object.keys(data).map((key) => ({
            account_id: key,
            ...data[key],
          }));

          setAccounts(formatted);
        } else {
          setError('Failed to load ad accounts');
        }
      } catch (err) {
        setError('Network error: ' + err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchAccounts();
  }, []);

  // Submit handler
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedAccount) {
      setError('Please select an ad account');
      return;
    }

    setSubmitting(true);
    setError(null);
    setSuccess(false);

    try {
      const formData = new FormData();
      formData.append('issetFacebookMetaAdAccountID', 'true');
      formData.append('facebook_meta_ad_account_id', selectedAccount);

      const response = await fetch(
        `${config.reactApi_baseUrl}/react-api/integrations/facebook-meta-ad-account-update.php`,
        {
          method: 'POST',
          body: formData,
          credentials: 'include',
        }
      );

      const data = await response.json();

      if (data.status === true) {
        setSuccess(true);

        setTimeout(() => {
          window.location.href = "/advertising";
        }, 1200);
      } else {
        const errMsg =
          getFirstErrorMessage(data) ||
          data.errorMsg || 'Failed to save changes';
        setError(errMsg);
      }
    } catch (err) {
      setError('Submit error: ' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Loader
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Alerts */}
      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Changes saved successfully! Redirecting...
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Card elevation={3} sx={{ overflow: 'visible', px: 5 }}>
          
          {/* Logo */}
          <Box sx={{ textAlign: 'center', pt: 5, pb: 3 }}>
            <img
              src={`${config.reactApi_baseUrl}/media/images/meta-facebook.png`}
              alt="Facebook Meta"
              style={{ width: 150, height: 150, objectFit: 'contain' }}
            />
          </Box>

          <CardContent sx={{ px: 4, pb: 4 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Facebook Meta Ad Accounts
            </Typography>

            <FormControl component="fieldset" fullWidth>
              <RadioGroup
                name="facebook_meta_ad_account_id"
                value={selectedAccount}
                onChange={(e) => setSelectedAccount(e.target.value)}
              >
                {accounts.length === 0 ? (
                  <Typography color="text.secondary" align="center" py={4}>
                    No ad accounts available.
                  </Typography>
                ) : (
                  accounts.map((acc) => (
                    <Card
                      key={acc.account_id}
                      variant="outlined"
                      sx={{
                        mb: 2,
                        borderColor:
                          selectedAccount === acc.id
                            ? 'primary.main'
                            : 'grey.300',
                        bgcolor:
                          selectedAccount === acc.id
                            ? 'action.selected'
                            : 'white',
                        transition: '0.2s',
                        '&:hover': {
                          borderColor: 'primary.light',
                        },
                      }}
                    >
                      <FormControlLabel
                        value={acc.id}
                        control={<Radio sx={{ ml: 1 }} />}
                        label={
                          <Box sx={{ flex: 1, p: 1 }}>
                            <Typography variant="subtitle1" fontWeight="bold">
                              {acc.name}
                            </Typography>

                            <Typography variant="body2" color="text.secondary">
                              {acc.account_id}
                            </Typography>

                            <Typography variant="body2" color="text.secondary">
                              {acc.currency} • {acc.timezone_name}
                            </Typography>
                          </Box>
                        }
                        sx={{
                          width: '100%',
                          m: 0,
                          alignItems: 'center',
                        }}
                      />
                    </Card>
                  ))
                )}
              </RadioGroup>
            </FormControl>

            {/* Submit Button */}
            <Box sx={{ textAlign: 'right', mt: 4 }}>
              <Button
                type="submit"
                variant="contained"
                disabled={submitting || !selectedAccount}
                sx={{
                  bgcolor: '#e91e63',
                  '&:hover': { bgcolor: '#c2185b' },
                  px: 4,
                  py: 1.2,
                  textTransform: 'none',
                  fontWeight: 'bold',
                }}
              >
                {submitting ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Save Changes'
                )}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </form>
    </Box>
  );
};

export default FacebookMetaAdAccounts;